import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import { useLocation } from "react-router-dom";
import { NAVBAR_HEIGHT, NavDesktop } from "../Navbar/NavDesktop";
import { NavMobile } from "../Navbar/NavMobile";
import { Sidebar, DRAWER_WIDTH, DRAWER_WIDTH_CLOSED } from "../Navbar/Sidebar";
import useScreenSize from "../../hooks/useScreenSize";
import { PageHeader } from "../PageHeader/PageHeader";
import { Breadcrumbs } from "../Breadcrumbs/Breadcrumbs";
import { usePageMetadataContext } from "../../context/PageMetadataContext";
import { PageUid, useCustomisation } from "../../context/CustomisationContext";
import { resolvePageStyle } from "../../utils/pageStyleResolver";
import { AssignmentLayoutContext } from "../../context/AssignmentLayoutContext";
import { LayoutControlContext } from "../../context/LayoutControlContext";
import { usePageCustomisation } from "../../hooks/usePageCustomisation";
import { hasSomeCustomisation } from "../../utils/helpers";
import { GlobalLoader } from "../GlobalLoader/GlobalLoader";
import { gray } from "../../theme/themePrimitives";
import { useTranslate } from "../../hooks/useTranslate";
import { PackageType } from "../../services/api/types/assignments/assignment";

export type MessageType = "none" | "success" | "error";

const ASSIGNMENT_SIDEBAR_WIDTH_CLOSED = 56;

interface PageLayoutProps {
  children: React.ReactNode;
  initialSidebarOpen?: boolean;
  showMobileNav?: boolean;
  mobileNavTitle?: string;
  contentSx?: React.CSSProperties | any;
  isErrorComponent?: boolean;
  sidebarProps?: {
    showMessage?: MessageType;
    setShowMessage?: React.Dispatch<React.SetStateAction<MessageType>>;
    assignmentDataFilled?: boolean;
    setOpenMessageModal?: React.Dispatch<React.SetStateAction<boolean>>;
    setNavigateTo?: React.Dispatch<React.SetStateAction<string>>;
  };
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  children,
  initialSidebarOpen = false,
  showMobileNav,
  mobileNavTitle,
  contentSx,
  sidebarProps,
  isErrorComponent = false,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(initialSidebarOpen);
  const {
    breadcrumbs: pageBreadcrumbs,
    pageMetadata,
    pageStyle,
  } = usePageMetadataContext();
  usePageCustomisation("GPC");
  const { isMobile, isTablet } = useScreenSize();
  const isMobileView = showMobileNav ?? (isMobile || isTablet);
  const location = useLocation();
  const t = useTranslate("appFrame");
  const { customisations, getCustomisationValue } = useCustomisation();
  const isGpcLoaded = hasSomeCustomisation(
    customisations.customisations,
    "GPC",
  );

  const [contextDisableNavigation, setContextDisableNavigation] =
    useState(false);

  const navigationDisabled = isErrorComponent || contextDisableNavigation;

  const isRunReportRoute = [
    "/reports/user-report",
    "/reports/user-activity-report",
    "/reports/assignment-activity-report",
    "/reports/assesment-report",
  ].some((route) => location.pathname.includes(route));

  const isAssignmentRoute = location.pathname.startsWith("/assignment");
  const isProfileOrCertificates =
    location.pathname.startsWith("/profile") ||
    location.pathname.startsWith("/certificates");

  // Assignment desktop: right sidebar (course content) aligns with the main
  // content's top edge; state for context
  const [assignmentSidebarContent, setAssignmentSidebarContent] =
    useState<React.ReactNode>(null);
  const [assignmentCollapsedContent, setAssignmentCollapsedContent] =
    useState<React.ReactNode>(null);
  const onExpandDefaultTabRef = useRef<(() => void) | null>(null);
  const setOnExpandDefaultTab = useCallback((handler: (() => void) | null) => {
    onExpandDefaultTabRef.current = handler;
  }, []);
  const [assignmentSidebarOpen, setAssignmentSidebarOpen] = useState(false);
  const [assignmentPackageType, setAssignmentPackageType] = useState<
    PackageType | undefined
  >(undefined);
  const assignmentSidebarWidth = assignmentSidebarOpen
    ? "22%"
    : `${ASSIGNMENT_SIDEBAR_WIDTH_CLOSED}px`;
  useEffect(() => {
    if (!isAssignmentRoute) {
      setAssignmentSidebarContent(null);
      setAssignmentCollapsedContent(null);
      setAssignmentSidebarOpen(false);
      setAssignmentPackageType(undefined);
    } else {
      setAssignmentSidebarOpen(false);
      setSidebarOpen(false);
    }
  }, [location.pathname, isAssignmentRoute]);

  const useAssignmentGridLayout =
    isAssignmentRoute && !isMobileView && !isTablet;

  const assignmentContentRef = useRef<HTMLDivElement>(null);
  const [assignmentContentTop, setAssignmentContentTop] = useState(() =>
    parseInt(NAVBAR_HEIGHT, 10),
  );

  const contentMarginLeft = isMobileView
    ? 0
    : sidebarOpen
      ? DRAWER_WIDTH
      : DRAWER_WIDTH_CLOSED;

  // Check if we're on the home route
  const isHomeRoute = location.pathname.startsWith("/home");
  const isProfileRoute = location.pathname.startsWith("/profile");
  const isCertificatesRoute = location.pathname.startsWith("/certificates");
  // Check if we're on an error route (to disable page-specific background)
  const isErrorRoute = location.pathname.startsWith("/error");

  const designPageUid: PageUid | null = isCertificatesRoute
    ? "CERTIFICATES"
    : isProfileRoute
      ? "PROFILE"
      : null;

  const resolvedTitleColor = designPageUid
    ? resolvePageStyle(designPageUid, getCustomisationValue).titleColor
    : pageStyle?.titleColor;

  const isPageDesignReady = designPageUid
    ? hasSomeCustomisation(customisations.customisations, designPageUid)
    : true;

  useLayoutEffect(() => {
    if (!useAssignmentGridLayout) return;
    const measure = () => {
      const top = assignmentContentRef.current?.getBoundingClientRect().top;
      if (top != null) setAssignmentContentTop(top);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [
    useAssignmentGridLayout,
    pageBreadcrumbs,
    pageMetadata?.title,
    isPageDesignReady,
  ]);

  const pageBackgroundSettings = useMemo(() => {
    if (!isHomeRoute && !isProfileRoute && !isCertificatesRoute) return null;

    const {
      HOME: { backgroundSettings: homeBg },
      PROFILE: { backgroundSettings: profileBg },
      CERTIFICATES: { backgroundSettings: certBg },
      GPC: { backgroundSettings: gpcBg },
    } = customisations.customisations;

    // Pick which page settings to use based on route
    const pageBg = isHomeRoute
      ? homeBg
      : isProfileRoute
        ? profileBg
        : isCertificatesRoute
          ? certBg
          : null;

    if (!pageBg && !gpcBg) return null;

    // Read the correct keys explicitly based on route
    const backgroundColor = isHomeRoute
      ? (pageBg?.homePrimaryBgColor ?? gpcBg?.gpcPrimaryBgColor)
      : isProfileRoute
        ? (pageBg?.profilePrimaryBgColor ?? gpcBg?.gpcPrimaryBgColor)
        : isCertificatesRoute
          ? (pageBg?.certificatesPrimaryBgColor ?? gpcBg?.gpcPrimaryBgColor)
          : gpcBg?.gpcPrimaryBgColor;

    const backgroundImageToggle = isHomeRoute
      ? pageBg?.homeBackgroundImageToggle === "true"
      : isProfileRoute
        ? pageBg?.profileBackgroundImageToggle === "true"
        : isCertificatesRoute
          ? pageBg?.certificatesBackgroundImageToggle === "true"
          : false;

    const backgroundImage = isHomeRoute
      ? pageBg?.homeBackgroundImage
      : isProfileRoute
        ? pageBg?.profileBackgroundImage
        : isCertificatesRoute
          ? pageBg?.certificatesBackgroundImage
          : undefined;

    const shouldUseBackgroundImage =
      backgroundImageToggle && !!backgroundImage?.trim();

    const backgroundImageTransparency = isHomeRoute
      ? (pageBg?.homeBackgroundImageTransparency ??
        gpcBg?.gpcBackgroundImageTransparency)
      : isProfileRoute
        ? (pageBg?.profileBackgroundImageTransparency ??
          gpcBg?.gpcBackgroundImageTransparency)
        : isCertificatesRoute
          ? (pageBg?.certificatesBackgroundImageTransparency ??
            gpcBg?.gpcBackgroundImageTransparency)
          : gpcBg?.gpcBackgroundImageTransparency;

    const transparencyPercentage = backgroundImageTransparency
      ? Number(backgroundImageTransparency) / 100
      : 0;

    return {
      backgroundColor,
      backgroundImage: shouldUseBackgroundImage ? `/${backgroundImage}` : null,
      transparencyPercentage,
      shouldUseBackgroundImage,
    };
  }, [
    isHomeRoute,
    isProfileRoute,
    isCertificatesRoute,
    customisations.customisations,
  ]);

  const mainBackgroundColor = isErrorComponent
    ? "#F3F4F6"
    : isAssignmentRoute
      ? "white"
      : isRunReportRoute
        ? "#2B2B2B"
        : pageBackgroundSettings?.shouldUseBackgroundImage
          ? "transparent"
          : pageBackgroundSettings?.backgroundColor || gray[100];

  if (!isGpcLoaded) return <GlobalLoader />;

  return (
    <LayoutControlContext.Provider
      value={{
        isErrorComponent: navigationDisabled,
        setIsErrorComponent: setContextDisableNavigation,
      }}
    >
      <Box
        sx={{
          height: "100vh",
          "@supports (height: 100dvh)": { height: "100dvh" },
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          // backgroundColor: gray[100],
          position: "relative",
        }}
      >
        {pageBackgroundSettings?.shouldUseBackgroundImage && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              marginLeft: `${contentMarginLeft}px`,
              marginTop: isMobileView ? 0 : NAVBAR_HEIGHT,
              padding: useAssignmentGridLayout
                ? "0 0 0 32px"
                : isAssignmentRoute
                  ? "12px 32px 32px 32px"
                  : "32px",
              backgroundImage: `url(${pageBackgroundSettings.backgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: -1,
              "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: `rgba(0,0,0,${pageBackgroundSettings.transparencyPercentage})`,
              },
            }}
          />
        )}
        {isMobileView ? (
          <NavMobile
            title={
              (mobileNavTitle ?? pageMetadata?.title) ||
              (isHomeRoute ? t("mainMenu", "home") : undefined)
            }
            setSidebarOpen={setSidebarOpen}
          />
        ) : (
          <NavDesktop
            sidebarOpen={sidebarOpen}
            isErrorComponent={navigationDisabled}
            pageBgColor={mainBackgroundColor}
          />
        )}
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          mobileView={isMobileView}
          isErrorComponent={navigationDisabled}
          {...sidebarProps}
        />
        <Box
          component="main"
          sx={{
            marginLeft: `${contentMarginLeft}px`,
            marginRight: useAssignmentGridLayout ? assignmentSidebarWidth : 0,
            marginTop: isMobileView ? 0 : NAVBAR_HEIGHT,
            flex: 1,
            padding: useAssignmentGridLayout
              ? "0 0 0 32px"
              : isAssignmentRoute
                ? isMobileView
                  ? "0px"
                  : "12px 32px 32px 32px"
                : "32px",
            position: "relative",
            zIndex: 1,
            backgroundColor: mainBackgroundColor,
            transition: (theme) =>
              theme.transitions.create(["margin-left", "margin-right"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            ...(useAssignmentGridLayout
              ? {
                  overflow: "hidden",
                  height: `calc(100vh - ${NAVBAR_HEIGHT})`,
                  "@supports (height: 100dvh)": {
                    height: `calc(100dvh - ${NAVBAR_HEIGHT})`,
                  },
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }
              : {
                  overflow: "auto",
                  minHeight: `calc(100vh - ${NAVBAR_HEIGHT})`,
                  "@supports (height: 100dvh)": {
                    minHeight: `calc(100dvh - ${NAVBAR_HEIGHT})`,
                  },
                }),
            ...contentSx,
          }}
        >
          {useAssignmentGridLayout ? (
            <AssignmentLayoutContext.Provider
              value={{
                sidebarContent: assignmentSidebarContent,
                setSidebarContent: setAssignmentSidebarContent,
                sidebarOpen: assignmentSidebarOpen,
                setSidebarOpen: setAssignmentSidebarOpen,
                packageType: assignmentPackageType,
                setPackageType: setAssignmentPackageType,
                collapsedContent: assignmentCollapsedContent,
                setCollapsedContent: setAssignmentCollapsedContent,
                setOnExpandDefaultTab,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  pt: 4,
                  minWidth: 0,
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  {!isErrorRoute &&
                    pageBreadcrumbs &&
                    pageBreadcrumbs?.length > 0 && (
                      <Breadcrumbs
                        items={pageBreadcrumbs}
                        previewText={
                          !pageMetadata?.title
                            ? pageMetadata?.previewText
                            : undefined
                        }
                      />
                    )}
                  {!isErrorRoute &&
                    pageMetadata?.title &&
                    isPageDesignReady && (
                      <PageHeader
                        title={pageMetadata.title}
                        titleColor={resolvedTitleColor}
                        previewText={pageMetadata.previewText}
                        titleBadge={pageMetadata.titleBadge}
                        titleBadgeVariant={pageMetadata.titleBadgeVariant}
                      />
                    )}
                </Box>
                <Box
                  ref={assignmentContentRef}
                  sx={{
                    minWidth: 0,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {children}
                </Box>
              </Box>
            </AssignmentLayoutContext.Provider>
          ) : (
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                maxWidth: isProfileOrCertificates ? 1440 : undefined,
                margin: isProfileOrCertificates ? "0 auto" : undefined,
                color: isProfileOrCertificates
                  ? pageStyle?.textColor
                  : undefined,
              }}
            >
              {!isErrorRoute &&
                pageBreadcrumbs &&
                pageBreadcrumbs?.length > 0 &&
                !isAssignmentRoute && <Breadcrumbs items={pageBreadcrumbs} />}
              {!isErrorRoute &&
                pageMetadata?.title &&
                !isMobileView &&
                isPageDesignReady && (
                  <PageHeader
                    title={pageMetadata.title}
                    titleColor={resolvedTitleColor}
                    previewText={pageMetadata.previewText}
                    titleBadge={pageMetadata.titleBadge}
                    titleBadgeVariant={pageMetadata.titleBadgeVariant}
                  />
                )}
              {children}
            </Box>
          )}
        </Box>
        {useAssignmentGridLayout && (
          <Box
            sx={{
              position: "fixed",
              top: assignmentContentTop,
              right: 0,
              height: `calc(100vh - ${assignmentContentTop}px)`,
              width: assignmentSidebarWidth,
              zIndex: 900,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderLeft: "1px solid #E5E7EB",
              borderTop: "1px solid #E5E7EB",
              visibility:
                assignmentPackageType === undefined ? "hidden" : "visible",
              backgroundColor: "#fff",
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: assignmentSidebarOpen
                    ? theme.transitions.duration.enteringScreen
                    : theme.transitions.duration.leavingScreen,
                }),
            }}
          >
            {assignmentSidebarOpen ? (
              <Box
                sx={{
                  minWidth: 0,
                  minHeight: 0,
                  overflow: "hidden",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    flexShrink: 0,
                    borderBottom: `1px solid ${gray[200]}`,
                    px: 2,
                    py: 1,
                  }}
                >
                  <IconButton
                    aria-label="Collapse sidebar"
                    onClick={() => setAssignmentSidebarOpen(false)}
                    sx={{
                      width: 28,
                      height: 28,
                      padding: 0,
                      color: gray[500],
                      border: "none",
                      backgroundColor: "transparent",
                      "&:hover": {
                        color: gray[900],
                        backgroundColor: "transparent",
                        borderColor: "transparent",
                      },
                    }}
                  >
                    <ChevronLeftIcon
                      sx={{ fontSize: 20, transform: "scaleX(-1)" }}
                    />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    minWidth: 0,
                    minHeight: 0,
                    overflow: "hidden",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {assignmentSidebarContent}
                </Box>
              </Box>
            ) : (
              assignmentCollapsedContent && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    pt: 6.5,
                  }}
                >
                  <Box
                    sx={{
                      width: "calc(100% - 24px)",
                      borderTop: `1px solid ${gray[200]}`,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 3,
                      pt: 3,
                    }}
                  >
                    {assignmentCollapsedContent}
                  </Box>
                </Box>
              )
            )}
          </Box>
        )}
        {useAssignmentGridLayout && !assignmentSidebarOpen && (
          <IconButton
            aria-label="Expand sidebar"
            onClick={() => {
              setAssignmentSidebarOpen(true);
              onExpandDefaultTabRef.current?.();
            }}
            sx={{
              position: "fixed",
              top: assignmentContentTop + 28,
              right: `${(ASSIGNMENT_SIDEBAR_WIDTH_CLOSED - 28) / 2}px`,
              transform: "translate(0, -50%)",
              zIndex: 901,
              visibility:
                assignmentPackageType === undefined ? "hidden" : "visible",
              flexShrink: 0,
              width: 28,
              height: 28,
              padding: 0,
              color: gray[500],
              border: "none",
              backgroundColor: "transparent",
              "&:hover": {
                color: gray[900],
                backgroundColor: "transparent",
                borderColor: "transparent",
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>
    </LayoutControlContext.Provider>
  );
};
